// Client-side port of the backend PromptPay EMV builder
// (backend/flask_application/apps/views/_model.py: build_promptpay_emv_payload,
// _emv_crc16, normalize_promptpay_account_number). Output must stay
// byte-identical to the backend; tests/fixtures/promptpay-backend-vectors.json
// is generated from the real backend functions and checked against this file.

const ACCOUNT_PATTERN = /^(?:0\d{9}|\d{13})$/;
const DECIMAL_PATTERN = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:e([+-]?\d+))?$/i;

export function normalizePromptPayAccountNumber(accountNumber) {
  return String(accountNumber === null || accountNumber === undefined ? '' : accountNumber).replace(/[\s-]/g, '');
}

function tlv(tag, value) {
  const text = String(value);
  return tag + (text.length < 10 ? '0' : '') + text.length + text;
}

// CRC-16/CCITT-FALSE, four uppercase hex digits.
export function emvCrc16(payload) {
  let checksum = 0xffff;
  for (let i = 0; i < payload.length; i += 1) {
    checksum ^= (payload.charCodeAt(i) & 0xff) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      checksum = checksum & 0x8000 ? ((checksum << 1) ^ 0x1021) & 0xffff : (checksum << 1) & 0xffff;
    }
  }
  return checksum.toString(16).toUpperCase().padStart(4, '0');
}

function incrementDigits(digits) {
  const chars = digits.split('');
  for (let i = chars.length - 1; i >= 0; i -= 1) {
    if (chars[i] === '9') {
      chars[i] = '0';
    } else {
      chars[i] = String(Number(chars[i]) + 1);
      return chars.join('');
    }
  }
  return '1' + chars.join('');
}

// Mirrors Decimal(str(amount)).quantize(Decimal('0.01'), ROUND_HALF_UP) using
// string arithmetic so no binary floating point rounding is involved. Returns
// a "123.45" string, or null when Python would raise / the amount is not > 0.
export function toTwoDecimals(amount) {
  const match = DECIMAL_PATTERN.exec(String(amount).trim());
  if (!match) return null;
  const negative = match[1] === '-';
  let whole = match[2] !== undefined ? match[2] : '0';
  let fraction = match[2] !== undefined ? match[3] || '' : match[4];
  const exponent = match[5] ? parseInt(match[5], 10) : 0;

  if (exponent > 0) {
    fraction = fraction.padEnd(exponent, '0');
    whole += fraction.slice(0, exponent);
    fraction = fraction.slice(exponent);
  } else if (exponent < 0) {
    const shift = -exponent;
    whole = whole.padStart(shift + 1, '0');
    fraction = whole.slice(whole.length - shift) + fraction;
    whole = whole.slice(0, whole.length - shift);
  }

  whole = whole.replace(/^0+(?=\d)/, '');
  let cents = fraction.slice(0, 2).padEnd(2, '0');
  const roundUp = fraction.length > 2 && fraction.charCodeAt(2) >= 53; // '5'
  let combined = whole + cents;
  if (roundUp) combined = incrementDigits(combined);
  combined = combined.replace(/^0+(?=\d{3})/, '');
  combined = combined.padStart(3, '0');
  cents = combined.slice(-2);
  whole = combined.slice(0, -2);

  if (negative || /^0+$/.test(whole + cents)) return null;
  return whole + '.' + cents;
}

// Returns '' whenever the backend returns '' (invalid account or amount).
export function buildPromptPayPayload(accountNumber, amount) {
  const account = normalizePromptPayAccountNumber(accountNumber);
  if (!ACCOUNT_PATTERN.test(account)) return '';
  if (amount === null || amount === undefined || typeof amount === 'boolean') return '';
  const amountText = toTwoDecimals(amount);
  if (amountText === null) return '';

  const proxy = account.length === 10 ? tlv('01', '0066' + account.slice(1)) : tlv('02', account);
  const merchantAccount = tlv('29', tlv('00', 'A000000677010111') + proxy);
  const payload =
    tlv('00', '01') +
    tlv('01', '12') +
    merchantAccount +
    tlv('53', '764') +
    tlv('54', amountText) +
    tlv('58', 'TH') +
    '6304';
  return payload + emvCrc16(payload);
}
